import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

const inputStyle = { width: "100%", padding: "10px 14px", borderRadius: 9, border: "0.5px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", fontSize: 14, color: "#f0efe9", outline: "none", boxSizing: "border-box" };

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [view, setView] = useState('login'); // 'login' | 'forgot' | 'sent'
  const [form, setForm] = useState({ email: '', password: '' });
  const [fpEmail, setFpEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.email || !form.password) { setError('Please fill in all fields.'); return; }
    setLoading(true);
    setError('');
    try {
      const data = await api.login(form);
      login({ ...data.user, token: data.token });
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!fpEmail) { setError('Please enter your email address.'); return; }
    setLoading(true);
    setError('');
    try {
      await api.forgotPassword({ email: fpEmail });
      setView('sent');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const logo = (
    <button onClick={() => navigate('/')} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", marginBottom: 40 }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#7F77DD,#1D9E75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>F</span>
      </div>
      <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.8px", color: "#f0efe9" }}>FLO</span>
    </button>
  );

  const card = { width: "100%", maxWidth: 400, background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: "36px 32px" };
  const linkBtn = { background: "none", border: "none", color: "#AFA9EC", cursor: "pointer", fontSize: 13, fontWeight: 500 };
  const wrap = { minHeight: "100vh", background: "#0e0e0c", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 };

  if (view === 'sent') {
    return (
      <div style={wrap}>
        {logo}
        <div style={card}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#f0efe9", marginBottom: 6, letterSpacing: "-0.5px" }}>Check your email</h1>
          <p style={{ fontSize: 13, color: "rgba(240,239,233,0.55)", marginBottom: 24, lineHeight: 1.6 }}>
            If an account exists for <strong style={{ color: "#f0efe9" }}>{fpEmail}</strong>, a password reset link has been sent. Check your inbox and spam folder.
          </p>
          <button onClick={() => { setView('login'); setFpEmail(''); setError(''); }} style={linkBtn}>← Back to sign in</button>
        </div>
      </div>
    );
  }

  if (view === 'forgot') {
    return (
      <div style={wrap}>
        {logo}
        <div style={card}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#f0efe9", marginBottom: 6, letterSpacing: "-0.5px" }}>Forgot password?</h1>
          <p style={{ fontSize: 13, color: "rgba(240,239,233,0.45)", marginBottom: 28 }}>Enter your email and we'll send a reset link.</p>

          <div>
            <div style={{ fontSize: 11, fontWeight: 500, color: "rgba(240,239,233,0.5)", marginBottom: 5 }}>Email</div>
            <input value={fpEmail} onChange={e => setFpEmail(e.target.value)} type="email" placeholder="you@agencymail.com" onKeyDown={e => e.key === 'Enter' && handleForgotPassword()} style={inputStyle} />
          </div>

          {error && <div style={{ marginTop: 12, fontSize: 12, color: "#F09595", padding: "8px 12px", borderRadius: 7, background: "rgba(226,75,74,0.1)", border: "0.5px solid rgba(226,75,74,0.2)" }}>{error}</div>}

          <button onClick={handleForgotPassword} disabled={loading} style={{ marginTop: 20, width: "100%", padding: "11px", borderRadius: 9, border: "none", background: "#534AB7", color: "#fff", fontSize: 14, cursor: "pointer", fontWeight: 600, opacity: loading ? 0.7 : 1 }}>
            {loading ? "Sending..." : "Send reset link"}
          </button>

          <div style={{ marginTop: 20, textAlign: "center" }}>
            <button onClick={() => { setView('login'); setError(''); }} style={linkBtn}>← Back to sign in</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      {logo}
      <div style={card}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#f0efe9", marginBottom: 6, letterSpacing: "-0.5px" }}>Welcome back</h1>
        <p style={{ fontSize: 13, color: "rgba(240,239,233,0.45)", marginBottom: 28 }}>Sign in to your FLO account</p>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, color: "rgba(240,239,233,0.5)", marginBottom: 5 }}>Email</div>
            <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} type="email" placeholder="you@agencymail.com" style={inputStyle} />
          </div>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
              <span style={{ fontSize: 11, fontWeight: 500, color: "rgba(240,239,233,0.5)" }}>Password</span>
              <button onClick={() => { setView('forgot'); setFpEmail(form.email); setError(''); }} style={{ ...linkBtn, fontSize: 11 }}>Forgot password?</button>
            </div>
            <input value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} type="password" placeholder="••••••••" onKeyDown={e => e.key === 'Enter' && handleSubmit()} style={inputStyle} />
          </div>
        </div>

        {error && <div style={{ marginTop: 12, fontSize: 12, color: "#F09595", padding: "8px 12px", borderRadius: 7, background: "rgba(226,75,74,0.1)", border: "0.5px solid rgba(226,75,74,0.2)" }}>{error}</div>}

        <button onClick={handleSubmit} disabled={loading} style={{ marginTop: 20, width: "100%", padding: "11px", borderRadius: 9, border: "none", background: "#534AB7", color: "#fff", fontSize: 14, cursor: "pointer", fontWeight: 600, opacity: loading ? 0.7 : 1 }}>
          {loading ? "Signing in..." : "Sign in"}
        </button>

        <div style={{ marginTop: 20, textAlign: "center", fontSize: 13, color: "rgba(240,239,233,0.4)" }}>
          Don't have an account?{" "}
          <button onClick={() => navigate('/signup')} style={linkBtn}>Sign up</button>
        </div>
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "0.5px solid rgba(255,255,255,0.07)", display: "flex", gap: 16, justifyContent: "center" }}>
          <button onClick={() => navigate('/privacy')} style={{ ...linkBtn, fontSize: 11, color: "rgba(240,239,233,0.3)" }}>Privacy Policy</button>
          <button onClick={() => navigate('/terms')} style={{ ...linkBtn, fontSize: 11, color: "rgba(240,239,233,0.3)" }}>Terms of Service</button>
        </div>
      </div>
    </div>
  );
}
